import React, { useEffect, useMemo, useState } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Tooltip,
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@fluentui/react-components";
import {
  Warning20Regular,
  Shield20Regular,
  People20Regular,
  Clipboard20Regular,
  Money20Regular,
  DataArea20Regular,
  Search20Regular,
  Add20Regular,
  Settings20Regular,
  Calendar20Regular,
  ChevronLeft20Regular,
  ChevronRight16Regular,
  ArrowLeft20Regular,
  ArrowRight20Regular,
  ArrowLeft16Regular,
  ArrowRight16Regular,
  TaskListLtr20Regular,
  Document20Regular,
  Call20Regular,
  Mail20Regular,
  MailInboxAll20Regular,
  Drafts20Regular,
  Folder20Regular,
  Send20Regular,
  Delete20Regular,
  Archive20Regular,
  Chat20Regular,
  Alert20Regular,
  Dismiss20Regular,
} from "@fluentui/react-icons";

import { useNavigate, useLocation } from "react-router";
import { trackTableRoute } from "../hooks/useTableNavState";
import { isReloadWarningSuppressed } from "../utils/reloadWarningSuppress";
import { useResponsive } from "../hooks/useResponsive";
import { useBildschirmTastatur } from "../hooks/useBildschirmTastatur";
import { usePersistentCollapsed } from "../hooks/usePersistentCollapsed";
import {
  buildMailFolderOrder,
  ordnerGesamtzahl,
  visibleFolderRows,
  zeigeVollenBaum,
  type MailFolderEntry,
} from "./mail/mailFolderTree";
import { canReadMailbox } from "../utils/mailboxAccess";
import { browserSpeicher, leseAufgeklappt, schreibeAufgeklappt } from "./mail/mailFolderExpansion";
import { useSmartBack } from "../hooks/smartBack";
import { useNavHistory } from "../hooks/navHistory";
import { useNavBadgesShared } from "../context/NavBadgesContext";
import { useCommandPalette } from "../context/CommandPaletteContext";
import CommandPalette from "./commandPalette/CommandPalette";
import { computeTopbarControls } from "./topbarControls";
import { useChatOptional } from "../context/ChatContext";
import LoginPage from "../pages/LoginPage";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import { presenceService, type DayCloseStatusDto } from "../services/presenceService";
import { isMentionNotif } from "./notifications/notifIcon";
import { MitteilungszentrumListe } from "./notifications/MitteilungszentrumListe";
import { clusterNotifications } from "./notifications/clusterNotifications";
import { pushService } from "../services/pushService";
import { NotificationSetupHinweis } from "./notifications/NotificationSetupHinweis";
import { UpdateHinweis } from "./update/UpdateHinweis";
import { clearAppSettingsSession, useAppSettings } from "../context/AppSettingsContext";
import WindowLayer from "./windowManager/WindowLayer";
import { UserCircle } from "./UserCircle";
import { UserMenuPopover } from "./UserMenuPopover";
import { useTelephony } from "../context/TelephonyContext";
import { useMailContext } from "../context/MailContext";
import {
  DVAG_BEREICH_TITEL,
  DVAG_POST_GEAENDERT,
  dvagOrdnerKey,
  dvagPostService,
  type DvagZaehler,
} from "../services/dvagPostService";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import { openComposeWindow } from "./mail/ComposeMailWindow";
import { displayName } from "../pages/profile/userDisplay";
import KmvsWordmark from "../assets/kmvs-wordmark.png";
import KmvsEmblem from "../assets/kmvs-emblem.png";
import MobileBottomNav from "./navigation/MobileBottomNav";
import { fmtBadge } from "./navigation/badgeFormat";
import { NeuAnlageKnopf } from "./neuAnlage/NeuAnlageKnopf";
import { MeldeButton } from "./tickets/MeldeButton";

import { Spinner } from "@fluentui/react-components";
import { CourteMarke } from "./CourteMarke";

// --- Navigations-Design (Courté-Brand): Plum-Verlauf-Sidebar, Gold-Akzent,
// Sektionsgliederung, Marken-Logo. Topbar bewusst hell (weiß/transluzent). ---
const NAV = {
  bgGradient: "linear-gradient(180deg, #3b2240 0%, #28182d 100%)",
  bg: "#321a37", // solide Plum-Mitte (Fallback / Phone-Drawer-Basis)
  text: "#ece3ea",
  textMuted: "#bca9b8",
  sectionLabel: "rgba(236,227,234,0.42)",
  hoverBg: "rgba(255,255,255,0.06)",
  activeBg: "rgba(255,167,20,0.13)",
  activeBorder: "rgba(255,167,20,0.45)",
  activeText: "#ffffff",
  accent: "#ffa714", // Gold-Akzent (Marke)
  divider: "rgba(255,167,20,0.22)",
  font: "'Hanken Grotesk', 'Segoe UI', Tahoma, sans-serif",
  fontHead: "'Playfair Display', Georgia, 'Times New Roman', serif",
  widthExpanded: 248,
  widthCollapsed: 76,
  rowHeight: 44,
} as const;

// Icons der fünf Standard-Ordner. Reihenfolge und Beschriftung kommen seit
// Cluster POSTFACH (G5) aus `STANDARD_FOLDER_ORDER` in mailFolderTree.ts —
// derselben Quelle, die auch das „Verschieben nach…"-Menü benutzt. Hier bleibt
// nur, was Ordner-Wissen nicht ist: das Icon je Alias.
const MAIL_FOLDER_ICONS: Record<string, React.ReactNode> = {
  inbox: <MailInboxAll20Regular />,
  drafts: <Drafts20Regular />,
  sentitems: <Send20Regular />,
  deleteditems: <Delete20Regular />,
  archive: <Archive20Regular />,
};

/**
 * Icon einer Ordner-Zeile: Standardzeilen tragen ihr eigenes, alle übrigen den
 * Ordner. TERMINE (P6): die virtuelle Zeile ist KEIN Ordner — sie bekommt das
 * Kalender-Icon, damit sie sich auf den ersten Blick von den echten Ordnern
 * unterscheidet (verschieben kann man dorthin nichts).
 */
const mailFolderIcon = (entry: MailFolderEntry): React.ReactNode =>
  entry.virtual
    ? <Calendar20Regular />
    : (entry.alias ? MAIL_FOLDER_ICONS[entry.alias] : null) ?? <Folder20Regular />;

// Topbar (hell): weiß-transluzent mit Plum-Titel.
const TOPBAR = {
  bg: "rgba(255,255,255,0.86)",
  border: "rgba(59,34,64,0.12)",
  title: "#3b2240",
  iconBtnBg: "#ffffff",
  iconBtnBorder: "rgba(59,34,64,0.14)",
  iconBtnFg: "#6f5c70",
  height: 64,
} as const;

export const NavigationLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { resetDashboardCards } = useAppSettings();
  const { isAuthenticated, isLoading, user, signOut, hasPermission, isAdmin } = useAuth();
  const { notifications, markAllRead: markAllNotifRead } = useNotifications();
  const { openSchnellwahl } = useTelephony();

  // Phone (≤768) → Overlay-Drawer; Tablet (769–1024) → eingeklappte Icon-Rail;
  // Desktop (>1024) → volle Sidebar. Siehe hooks/useResponsive.
  const { isPhone, isTablet, isPhoneOrTablet } = useResponsive();
  const tastatur = useBildschirmTastatur(isPhoneOrTablet);
  // Dokument festnageln, solange die App-Shell steht (index.css „kmvs-app-fest").
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add("kmvs-app-fest");
    return () => html.classList.remove("kmvs-app-fest");
  }, []);
  // G1 — Topbar-Aktionen je Viewport: Desktop volle Such-Pille + Telefon-Button;
  // Tablet kompaktes Such-Icon (öffnet die Command-Palette) OHNE Telefon-Button,
  // damit der Header nicht bricht; Phone weder Suche noch Telefon in der Topbar.
  const topbar = computeTopbarControls({ isPhone, isTablet });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Globale Verlaufs-Navigation für die Topbar-Buttons (ersetzt die per-View
  // „Zurück"-Buttons). Fällt auf das Dashboard zurück, wenn kein In-App-Verlauf.
  const smartBack = useSmartBack("/");
  // In-App-Verlaufsstack: liefert disabled-Zustände + grobe Ziel-Labels
  // („Zurück zu Kontakte"/„Vorwärts zu Kontakt") für die Topbar-Buttons.
  const navHist = useNavHistory();
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Abmelde-Sperre v1 (AC1): hartes In-App-Logout-Gate, wenn ungelesene
  // Mitteilungen offen sind. Beim Klick auf „Abmelden" wird statt des Logouts
  // dieses Modal gezeigt; der eigentliche Logout ist blockiert, bis der Nutzer
  // gelesen hat (oder ihn aus dem Modal erneut auslöst → dann frei).
  const [logoutGuardOpen, setLogoutGuardOpen] = useState(false);
  // Tagesabschluss-Status (AP14) — beim Logout-Versuch frisch geholt; null = noch
  // nicht/nicht ermittelbar (dann blockiert nur die Mitteilungs-Quelle).
  const [dayClose, setDayClose] = useState<DayCloseStatusDto | null>(null);
  // Steuert das Mitteilungszentrum-Popover (Glocke) — kontrolliert, damit das
  // Logout-Gate-Modal es per „Mitteilungen prüfen" öffnen kann.
  const [notifOpen, setNotifOpen] = useState(false);
  // Sidebar-Badges (Handlungsbedarf): offene Vorgänge / neue Mails / verpasste Anrufe.
  const navBadges = useNavBadgesShared();
  // PR4: Mail-Menü in der Sidebar. Ordnerdaten + Auswahl aus dem MailContext,
  // Compose über den WindowManager.
  const {
    mailboxes,
    unreadByMailbox,
    folderTrees,
    activeFolder,
    setActiveFolder,
    currentMailbox,
    setCurrentMailbox,
  } = useMailContext();
  const { openWindow, windows, hasMaximizedWindow, dismissTopMaximized } =
    useWindowManager();
  // B1 (nur DESKTOP): Bei offenem maximiertem (fullpage) Fenster fängt „Zurück"
  // ab und restauriert das oberste Fenster zu floating, statt zu navigieren
  // (history.state.idx bleibt intakt — keine rohe history-Manipulation,
  // Browser-Back unberührt). Auf Phone/Tablet rendert der WindowLayer jedes
  // Fenster ohnehin als Vollbild (state entkoppelt) → „un-maximieren zu floating"
  // wäre ein toter Klick; dort navigiert „Zurück" normal und der (mobil breiter
  // greifende) WindowNavigationGuard fragt beim Wegnavigieren nach.
  const goBack = React.useCallback(() => {
    if (hasMaximizedWindow && !isPhoneOrTablet) {
      dismissTopMaximized();
      return;
    }
    smartBack();
  }, [hasMaximizedWindow, isPhoneOrTablet, dismissTopMaximized, smartBack]);
  // Steuert, ob auf /emails das Mail-Menü ODER das Top-Level-Menü gezeigt wird
  // („Zurück zum Menü"). Beim Betreten von /emails wieder aufs Mail-Menü.
  const [mailMenuOpen, setMailMenuOpen] = useState(true);
  const { setOpen: setPaletteOpen } = useCommandPalette();
  // Ungelesene Chat-Nachrichten (interner Chat) für das Nav-Badge.
  const chatCtx = useChatOptional();
  const chatUnread = chatCtx?.total ?? 0;

  // Letzte Route global tracken — für die Tabellen-Zustands-Wiederherstellung
  // beim Detail-Rücksprung (siehe hooks/useTableNavState).
  useEffect(() => {
    trackTableRoute(location.pathname);
  }, [location.pathname]);

  // Mitteilungen erscheinen NICHT mehr als Toast in KMVS (User 02.11.2026):
  // Benachrichtigt wird nur noch über Browser/Windows (Web-Push, den das
  // Backend zu jeder Mitteilung schickt) — die Liste bleibt in Glocke und
  // Mitteilungszentrum.

  // Web-Push: bei bereits erteilter Erlaubnis still (re-)abonnieren; auf Klick einer
  // OS-Benachrichtigung (vom Service Worker) zur verlinkten Seite navigieren.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      pushService.ensureSubscribed().catch(() => undefined);
    }
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { type?: string; link?: string } | undefined;
      if (d?.type === "kmvs-notification-click" && d.link) navigate(d.link);
    };
    navigator.serviceWorker?.addEventListener?.("message", onMsg);
    return () => navigator.serviceWorker?.removeEventListener?.("message", onMsg);
  }, [isAuthenticated, navigate]);

  const unreadNotifications = notifications.filter((n) => isMentionNotif(n.type) && !n.readAt);
  // Aggregierte Live-Counts für die zwei Mitteilungszentrum-Zeilen (Sidebar-Quellen).
  const mailUnreadTotal = navBadges.mails;
  const missedCallsTotal = navBadges.calls;
  // Badge der Glocke: alle offenen Mitteilungen, die das (nach Themen
  // gegliederte) Mitteilungszentrum zeigt — nicht mehr nur die „Mentions".
  const glockenZahl = React.useMemo(
    () => clusterNotifications({ notifications }).anzahlMitteilungen,
    [notifications],
  );
  const noCenterItems = mailUnreadTotal === 0 && missedCallsTotal === 0 && glockenZahl === 0;
  const markAllNotificationsRead = () => {
    void markAllNotifRead();
  };

  // Spiegelt die aktuelle Zahl ungelesener Mitteilungen in einen Ref — so können
  // die Event-/Timer-Handler unten den Stand lesen, ohne sich bei jeder Änderung
  // neu zu registrieren.
  const unreadCountRef = React.useRef(0);
  unreadCountRef.current = unreadNotifications.length;

  // beforeunload (AC1d, generisch): Wenn ungelesene Mitteilungen offen sind und
  // der Nutzer Tab/Fenster schließt oder neu lädt, den nativen „Seite verlassen?"-
  // Dialog auslösen (returnValue setzen). HINWEIS / technische Realität: Der
  // angezeigte Text ist NICHT anpassbar (Browser zeigen einen generischen Dialog)
  // und das Schließen lässt sich NICHT hart erzwingen — anders als das In-App-
  // Logout-Gate (das hart blockiert). Sauber registriert/deregistriert.
  useEffect(() => {
    if (!isAuthenticated) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      // Einzelne Seiten (z. B. Haushaltebereinigung) können den Nag unterdrücken.
      if (isReloadWarningSuppressed()) return undefined;
      if (unreadCountRef.current > 0) {
        e.preventDefault();
        // Veraltete Browser verlangen das Setzen von returnValue auf einen
        // (ignorierten) String, um den Dialog zu zeigen.
        e.returnValue = "";
        return "";
      }
      return undefined;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isAuthenticated]);

  // 16-Uhr-Reminder (AC1a): einmal pro Tag gegen ~16:00 lokaler Zeit eine
  // Browser-/OS-Benachrichtigung „Mitteilungszentrum prüfen" auslösen, sofern
  // ungelesene Mitteilungen vorliegen. Tagesmarke in localStorage verhindert
  // Mehrfach-Auslösung am selben Tag. Check im bestehenden 60-s-Takt.
  useEffect(() => {
    if (!isAuthenticated) return;
    const STORAGE_KEY = "kmvs:reminder16:lastDate";
    const checkReminder = () => {
      const now = new Date();
      // ~16:00: Stunde 16, erste Minute, damit der 60-s-Takt das Fenster trifft.
      if (now.getHours() !== 16 || now.getMinutes() > 1) return;
      if (unreadCountRef.current <= 0) return;
      const today = now.toISOString().slice(0, 10); // YYYY-MM-DD (lokal grob)
      let last: string | null = null;
      try {
        last = localStorage.getItem(STORAGE_KEY);
      } catch {
        /* localStorage nicht verfügbar — Reminder dann nicht persistent */
      }
      if (last === today) return;
      try {
        localStorage.setItem(STORAGE_KEY, today);
      } catch {
        /* ignore */
      }
      const fire = () => {
        try {
          new Notification("Mitteilungszentrum prüfen", {
            body: "Bitte hake deine offenen Mitteilungen ab, bevor du Feierabend machst.",
            tag: "kmvs-reminder-16",
          });
        } catch {
          /* Notification-Konstruktor evtl. nicht erlaubt — still ignorieren */
        }
      };
      if (typeof Notification === "undefined") return;
      if (Notification.permission === "granted") {
        fire();
      } else if (Notification.permission === "default") {
        // Erlaubnis (still) anfragen; bei Zustimmung sofort feuern.
        Notification.requestPermission()
          .then((perm) => {
            if (perm === "granted") fire();
          })
          .catch(() => undefined);
      }
    };
    // Sofort einmal prüfen (falls die App genau um 16:00 schon offen ist) und
    // dann im 60-s-Takt.
    checkReminder();
    const t = window.setInterval(checkReminder, 60_000);
    return () => window.clearInterval(t);
  }, [isAuthenticated]);

  // Auto-Collapse: Routen mit maximalem Platzbedarf UND Tablets (damit die
  // Kontakte-Tabelle im Querformat die volle Breite bekommt) starten eingeklappt.
  useEffect(() => {
    const path = location.pathname;
    const isBeratermodus = /^\/contacts\/[^/]+\/beratermodus(\/|$)/.test(path);
    const isRisikoStep = /^\/contacts\/[^/]+\/risikoanalyse-step(\/|$)/.test(path);
    // PR4: /emails klappt NICHT mehr ein — das Mail-Menü braucht die Breite und
    // hat im Mail-Modus seinen eigenen Schalter (siehe effectiveCollapsed).
    const needsWideContent = isBeratermodus || isRisikoStep;
    setIsCollapsed(needsWideContent || isTablet);
  }, [location.pathname, isTablet]);

  // Beim Betreten von /emails wieder das Mail-Menü zeigen (nach „Zurück zum Menü"
  // bleibt es zu, solange man auf /emails ist — pathname ändert sich dabei nicht).
  useEffect(() => {
    if (location.pathname.startsWith("/emails")) setMailMenuOpen(true);
  }, [location.pathname]);

  // Mail-Modus aktiv = auf /emails UND Mail-Menü offen → Sidebar zeigt das
  // Mail-Menü, standardmäßig ausgeklappt (auch auf Tablet).
  const mailMode = location.pathname.startsWith("/emails") && mailMenuOpen;
  // Das Mail-Menü lässt sich einklappen (User 26.10.2026: „bei den Mails kann
  // ich das Menü nicht mehr einklappen" — der Knopf tat dort nichts, weil der
  // Mail-Modus das Ausklappen erzwang). Eigener, gemerkter Schalter: Wer die
  // Mailliste breit haben will, will das beim nächsten Mal auch.
  const [mailMenuEingeklappt, mailMenuKlappen] = usePersistentCollapsed(
    "kmvs.nav.mailMenuEingeklappt",
    false,
  );

  // DVAG-Kontakthistorie: Zähler für das Mail-Menü. `null` = nicht geladen oder
  // kein Zugriff (403) — dann erscheint der Abschnitt gar nicht.
  const [dvagZaehler, setDvagZaehler] = useState<DvagZaehler | null>(null);
  const aufMailSeite = location.pathname.startsWith("/emails");
  useEffect(() => {
    if (!aufMailSeite) return;
    let aktiv = true;
    const lade = () => {
      dvagPostService
        .zaehler()
        .then((z) => { if (aktiv) setDvagZaehler(z); })
        .catch(() => { if (aktiv) setDvagZaehler(null); });
    };
    lade();
    window.addEventListener(DVAG_POST_GEAENDERT, lade);
    return () => {
      aktiv = false;
      window.removeEventListener(DVAG_POST_GEAENDERT, lade);
    };
  }, [aufMailSeite]);
  // Im Mail-Modus zählt der eigene Schalter, sonst das normale Verhalten.
  const effectiveCollapsed = mailMode ? mailMenuEingeklappt : isCollapsed;

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isPhone) setDrawerOpen(false);
  };

  // Führt den eigentlichen Logout aus (ohne Gate). Vom Menüpunkt nur über das
  // Gate `requestLogout` erreichbar.
  const performLogout = async () => {
    setLogoutGuardOpen(false);
    clearAppSettingsSession();
    resetDashboardCards();
    await signOut();
  };

  // Abmelde-Sperre v1 (AC1c) + Tagesabschluss-Gate (AP14): hartes In-App-Gate vor
  // dem Logout. Blockiert, solange ungelesene Mitteilungen ODER offene Tages-
  // abschluss-Items (Entwürfe / fällige TWVs+Fristen) existieren — das Modal
  // nennt die Begründung. Der Tagesabschluss-Status wird bei JEDEM Versuch frisch
  // geholt (Re-Check), damit das Gate freigibt, sobald alles aufgelöst ist. Die
  // Item-Zähler kommen aus dem Plan-1-Aggregat (`presence/day-close-status`).
  const requestLogout = async () => {
    let status: DayCloseStatusDto | null = null;
    try {
      status = await presenceService.dayCloseStatus();
    } catch {
      // Aggregat nicht erreichbar → Tagesabschluss-Quelle ignorieren, nur die
      // (lokal bekannte) Mitteilungs-Quelle blockt. Kein harter Fehler.
      status = null;
    }
    setDayClose(status);
    const blockedByDayClose = !!status?.blocking;
    if (unreadNotifications.length > 0 || blockedByDayClose) {
      if (isPhone) setDrawerOpen(false);
      setLogoutGuardOpen(true);
      return;
    }
    await performLogout();
  };

  // T-364: Seit das Öffnen einer Mitteilung sie nicht mehr quittiert, stehen
  // geöffnete, aber nicht abgehakte Mitteilungen beim Abmelden noch offen. Damit
  // man sich nicht ausgesperrt fühlt, hakt das Gate sie auf Wunsch in einem
  // Schritt ab. Blockt sonst nichts (Tagesabschluss), geht es direkt weiter zur
  // Abmeldung; sonst bleibt das Modal mit den restlichen Punkten offen.
  const alleAbhakenAusGate = async () => {
    await markAllNotifRead();
    if (dayClose?.blocking) return;
    await performLogout();
  };

  // Aus dem Gate-Modal zum Mitteilungszentrum: Modal schließen und die Glocke
  // öffnen, damit der Nutzer die ungelesenen Mitteilungen prüfen kann.
  const goCheckNotifications = () => {
    setLogoutGuardOpen(false);
    setNotifOpen(true);
  };

  // Aus dem Gate-Modal zur Vorgänge-Liste (AP14): dort werden offene Entwürfe
  // aktiviert/verworfen bzw. fällige TWVs/Fristen erledigt/auf-später gesetzt —
  // über die bestehenden Plan-1-Listen/Aktionen (keine neuen Mutationen).
  const goCheckVorgaenge = () => {
    setLogoutGuardOpen(false);
    handleNavigate("/vorgaenge");
  };

  useEffect(() => {
    if (!isPhone) setDrawerOpen(false);
  }, [isPhone]);

  // Aktiven Menüpunkt aus dem Pfad ableiten (für Hervorhebung).
  const activeValue = useMemo(() => {
    const p = location.pathname;
    if (p === "/") return "dashboard";
    // VOR dem Kontakte-Zweig: „/co-intern" beginnt nicht mit „/contacts",
    // aber die Reihenfolge hier ist ohnehin sprechender.
    if (p.startsWith("/co-intern")) return "cointern";
    if (p.startsWith("/contacts") || p.startsWith("/kontakte")) return "kontakte";
    if (p.startsWith("/risks") || p.startsWith("/risiken")) return "risiko";
    if (p.startsWith("/contracts") || p.startsWith("/vertraege")) return "vertraege";
    if (p.startsWith("/damages")) return "schaden";
    // Detailrouten außerhalb der Bereichs-Prefixe: Schadenmappe (/schaeden/:id)
    // und die To-do-Weiterleitung (/todos/:id → /vorgaenge/:id) — sonst bleibt
    // die Topbar während der Weiterleitung leer/titellos.
    if (p.startsWith("/schaeden")) return "schaden";
    if (p.startsWith("/vorgaenge")) return "vorgaenge";
    if (p.startsWith("/todos")) return "vorgaenge";
    if (p.startsWith("/termine")) return "termine";
    if (p.startsWith("/emails")) return "schriftverkehr";
    if (p.startsWith("/anrufe")) return "anrufe";
    if (p.startsWith("/chat")) return "chat";
    if (p.startsWith("/zahlungsverkehr") || p.startsWith("/rechnungen")) return "rechnungen";
    if (p.startsWith("/alte-kundenakten")) return "alte-kundenakten";
    if (p.startsWith("/settings") || p.startsWith("/einstellungen")) return "einstellungen";
    return "";
  }, [location.pathname]);

  // Seitentitel (Playfair) für die Topbar — aus der aktiven Route abgeleitet.
  // Detailseiten (/x/:id) behalten den generischen Bereichstitel.
  const pageTitle = useMemo(() => {
    if (location.pathname.startsWith("/profil")) return "Profil";
    const map: Record<string, string> = {
      dashboard: "Dashboard",
      kontakte: "Kontakte",
      cointern: "CO intern",
      risiko: "Risiken",
      vertraege: "Verträge",
      schaden: "Schäden",
      vorgaenge: "Vorgänge",
      termine: "Termine",
      schriftverkehr: "Schriftverkehr",
      anrufe: "Telefon",
      chat: "Interner Chat",
      einstellungen: "Einstellungen",
      rechnungen: "Zahlungsverkehr",
      "alte-kundenakten": "Alte Kundenakten",
    };
    return map[activeValue] ?? "";
  }, [activeValue, location.pathname]);

  // Aufgeklappte Ordner, geschlüsselt als `postfach:ordner`. T-65: über das
  // Neuladen hinweg gespeichert — mit tiefen Unterordnern
  // (`Posteingang/5.Kundenmails/Team CLL/…`) war das ständige Neu-Aufklappen
  // lästig. Einträge gelöschter Ordner passen auf keine Zeile mehr und stören nicht.
  const [expandedFolders, setExpandedFolders] = React.useState<Set<string>>(() =>
    leseAufgeklappt(browserSpeicher()),
  );
  const toggleFolder = (mailbox: string, key: string) =>
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      const id = `${mailbox}:${key}`;
      if (next.has(id)) next.delete(id);
      else next.add(id);
      schreibeAufgeklappt(browserSpeicher(), next);
      return next;
    });

  if (isLoading) {
    return (
      <div className="kmvs-dvh-screen" style={{ width: "100vw", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Spinner size="huge" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Im Overlay (Phone) und beim Ausklappen sind Labels sichtbar; auf der
  // eingeklappten Rail nur Icons + Tooltip.
  type NavLeaf = {
    value: string;
    label: string;
    icon: React.ReactNode;
    onClick?: () => void;
    show: boolean;
    /** Optionaler Zähler (Handlungsbedarf) — wird in SP3/PR3 befüllt. */
    badge?: number;
  };

  const items: NavLeaf[] = [
    { value: "dashboard", label: "Dashboard", icon: <DataArea20Regular />, onClick: () => handleNavigate("/"), show: true },
    { value: "kontakte", label: "Kontakte", icon: <People20Regular />, onClick: () => handleNavigate("/contacts"), show: hasPermission("contacts:read") },
    { value: "risiko", label: "Risiken", icon: <Shield20Regular />, onClick: () => handleNavigate("/risiken"), show: hasPermission("risks:read") },
    { value: "vertraege", label: "Verträge", icon: <Clipboard20Regular />, onClick: () => handleNavigate("/vertraege"), show: hasPermission("contracts:read") },
    { value: "schaden", label: "Schäden", icon: <Warning20Regular />, onClick: () => handleNavigate("/damages"), show: hasPermission("damages:read") },
    { value: "vorgaenge", label: "Vorgänge", icon: <TaskListLtr20Regular />, onClick: () => handleNavigate("/vorgaenge"), show: hasPermission("vorgaenge:read"), badge: navBadges.vorgaenge },
    { value: "termine", label: "Termine", icon: <Calendar20Regular />, onClick: () => handleNavigate("/termine"), show: true },
    {
      value: "schriftverkehr",
      label: "Schriftverkehr",
      icon: <Mail20Regular />,
      onClick: () => {
        handleNavigate("/emails");
        setMailMenuOpen(true);
        // Schon in den Mails, Menü eingeklappt: Der Klick holt die Ordner zurück.
        if (mailMode && mailMenuEingeklappt) mailMenuKlappen();
      },
      show: true,
      badge: navBadges.mails,
    },
    { value: "anrufe", label: "Telefon", icon: <Call20Regular />, onClick: () => handleNavigate("/anrufe"), show: hasPermission("calls:read"), badge: navBadges.calls },
    { value: "chat", label: "Interner Chat", icon: <Chat20Regular />, onClick: () => handleNavigate("/chat"), show: true, badge: chatUnread },
  ];

  // „Einstellungen" liegt bewusst NICHT mehr in der linken Sidebar (SP7) — der
  // Zugang läuft ausschließlich über das Topbar-Zahnrad (→ /settings). Das
  // activeValue-/pageTitle-Mapping für 'einstellungen' bleibt erhalten, damit die
  // Route über die Topbar korrekt betitelt wird.
  const trailingItems: NavLeaf[] = [
    { value: "rechnungen", label: "Zahlungsverkehr", icon: <Money20Regular />, onClick: () => handleNavigate("/zahlungsverkehr"), show: hasPermission("invoices:read") },
    { value: "alte-kundenakten", label: "Alte Kundenakten", icon: <Document20Regular />, onClick: () => handleNavigate("/alte-kundenakten"), show: true },
  ];

  /**
   * „CO intern" (PO 09.09.2026) steht GANZ UNTEN, abgesetzt durch eine dünne
   * Linie — und nicht bei den Kontakten, obwohl es dieselbe Tabelle zeigt.
   *
   * Der Grund ist fachlich: Jeder andere Menüpunkt führt zu dem, womit die
   * Mitarbeiter arbeiten (Kunden, Verträge, Schäden). Dieser eine führt zu
   * COURTÉ SELBST — Lieferanten, Sponsoring, Mitgliedschaften,
   * Direktionspartner. Genau diese Trennung hat der PO verlangt („bitte nicht
   * damit vermischen, auf keinen Fall"); die Linie macht sie im Menü sichtbar.
   * Deshalb trägt der Punkt auch die Hausmarke statt eines Fluent-Symbols.
   *
   * Das Ausblenden ohne Recht ist reine Höflichkeit — durchgesetzt wird der
   * Zugriff im Backend (ohne `cointern:read` liefert die API reine CO-Kontakte
   * nirgends aus).
   */
  const abschlussItems: NavLeaf[] = [
    { value: "cointern", label: "CO intern", icon: <CourteMarke />, onClick: () => handleNavigate("/co-intern"), show: hasPermission("cointern:read") },
  ];

  // Phone-Bottom-Navigation: dieselbe Item-Quelle wie die Sidebar (EINE Wahrheit),
  // ohne „Zahlungsverkehr" (Entscheidung 16) und nur sichtbare Punkte.
  const phoneNavItems = [...items, ...trailingItems, ...abschlussItems].filter(
    (i) => i.show && i.value !== "rechnungen",
  );
  // Bei geöffnetem Vollbild-Fenster (Mail lesen/Compose/Termin) blendet die
  // Leiste aus (Entscheidung 12) — auf dem Phone rendert der WindowLayer solche
  // Fenster als inset:0-Panels; minimierte Fenster (nur Dock) zählen nicht.
  const hasFullscreenWindow = windows.some((w) => w.state !== "minimized" && !w.browserFenster);
  const showMobileBottomNav = isPhone && !hasFullscreenWindow;

  /** Eine Menüzeile (inset-Pille). `collapsed` → nur Icon + Tooltip; Badge → Punkt. */
  const NavRow: React.FC<{
    icon: React.ReactNode;
    label: string;
    active?: boolean;
    collapsed: boolean;
    onClick?: () => void;
    badge?: number;
    /**
     * T-264: Gesamtzahl der Mails eines eigenen Ordners. Bewusst getrennt von
     * `badge` (= ungelesen, Gold-Pille): grau und ohne Pille, damit sie nicht
     * als neue Post gelesen wird.
     */
    count?: number | null;
    /** Einrückung für verschachtelte Ordner (Mail-Menü). */
    indent?: number;
    /** Ordner hat Unterordner → Aufklapp-Pfeil davor. */
    expandable?: boolean;
    expanded?: boolean;
    onToggleExpand?: () => void;
  }> = ({ icon, label, active, collapsed, onClick, badge, count, indent, expandable, expanded, onToggleExpand }) => {
    const hasBadge = typeof badge === "number" && badge > 0;
    const badgeLabel = fmtBadge(badge); // Zahl → String, „99+" gekappt
    const badgeWide = !!badgeLabel && badgeLabel.length >= 3;
    const row = (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick?.();
          }
        }}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: collapsed ? 0 : 12,
          justifyContent: collapsed ? "center" : "flex-start",
          margin: collapsed ? "2px 12px" : "1px 8px",
          ...(!collapsed && indent ? { marginLeft: 8 + indent * 14 } : {}),
          // Auf dem Phone größere Touch-Targets, sonst die Standardhöhe.
          height: isPhone ? 50 : NAV.rowHeight,
          padding: collapsed ? "0" : "0 10px",
          borderRadius: 10,
          boxSizing: "border-box",
          cursor: "pointer",
          userSelect: "none",
          color: active ? NAV.activeText : NAV.text,
          fontFamily: NAV.font,
          fontSize: 14.5,
          fontWeight: active ? 600 : 500,
          backgroundColor: active ? NAV.activeBg : "transparent",
          boxShadow: active ? `inset 0 0 0 1px ${NAV.activeBorder}` : "none",
          transition: "background-color 120ms ease",
        }}
        onMouseEnter={(e) => {
          if (!active) (e.currentTarget as HTMLDivElement).style.backgroundColor = NAV.hoverBg;
        }}
        onMouseLeave={(e) => {
          if (!active) (e.currentTarget as HTMLDivElement).style.backgroundColor = "transparent";
        }}
      >
        {/* Goldener Akzentbalken am linken Sidebar-Rand (aktiv). */}
        {active && (
          <span
            aria-hidden
            style={{
              position: "absolute",
              left: collapsed ? -12 : -8,
              top: 8,
              bottom: 8,
              width: 3,
              borderRadius: "0 3px 3px 0",
              background: NAV.accent,
            }}
          />
        )}
        {/* Aufklapp-Pfeil für Ordner mit Unterordnern (Outlook-Verhalten).
            Eigener Klickbereich: Aufklappen darf den Ordner NICHT wechseln —
            man will oft nur hineinsehen, ohne die Liste neu zu laden. */}
        {!collapsed && expandable && (
          <span
            role="button"
            tabIndex={-1}
            aria-label={expanded ? "Unterordner zuklappen" : "Unterordner aufklappen"}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand?.();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 16,
              height: 16,
              marginLeft: -6,
              flexShrink: 0,
              cursor: "pointer",
              transform: expanded ? "rotate(90deg)" : "none",
              transition: "transform 0.12s",
              opacity: 0.75,
            }}
          >
            <ChevronRight16Regular />
          </span>
        )}
        {/* Platzhalter, damit Zeilen OHNE Pfeil auf derselben Linie stehen. */}
        {!collapsed && !expandable && indent !== undefined && (
          <span aria-hidden style={{ width: 16, marginLeft: -6, flexShrink: 0 }} />
        )}
        <span style={{ position: "relative", display: "flex", width: 24, height: 24, alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 20 }}>
          {icon}
          {/* Eingeklappt (Rail) ODER Phone (A7 revidiert): Mini-Zähler-Pille statt
              Punkt, „99+" gekappt; bei 3 Zeichen kleinere Schrift + mittig über
              der Ecke verankert, damit sie nicht aus dem Icon-Slot ausbricht. */}
          {(collapsed || isPhone) && badgeLabel && (
            <span
              aria-hidden
              style={{
                position: "absolute",
                top: -6,
                right: badgeWide ? -4 : -6,
                transform: badgeWide ? "translateX(25%)" : undefined,
                minWidth: 16,
                height: 16,
                padding: "0 4px",
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: NAV.accent,
                color: "#361836",
                fontSize: badgeWide ? 9 : 10,
                fontWeight: 700,
                lineHeight: 1,
                boxShadow: "0 0 0 2px #301836",
                boxSizing: "border-box",
              }}
            >
              {badgeLabel}
            </span>
          )}
        </span>
        {!collapsed && (
          <span style={{ flex: 1, textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {label}
          </span>
        )}
        {/* T-264: Gesamtzahl eines eigenen Ordners — gedämpft, ohne Pille und
            ungekappt (mit Tausenderpunkt). Steht nie neben einer Ungelesen-Pille. */}
        {!collapsed && !badgeLabel && typeof count === "number" && count > 0 && (
          <span
            title={`${count.toLocaleString("de-DE")} E-Mail${count === 1 ? "" : "s"} im Ordner`}
            style={{
              flexShrink: 0,
              fontSize: 12,
              fontWeight: 500,
              color: NAV.textMuted,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {count.toLocaleString("de-DE")}
          </span>
        )}
        {/* Ausgeklappt (Desktop/Tablet): goldene Zähler-Pille rechts, „99+" gekappt. */}
        {!collapsed && !isPhone && badgeLabel && (
          <span
            style={{
              flexShrink: 0,
              minWidth: 20,
              height: 20,
              padding: "0 6px",
              borderRadius: 999,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: NAV.accent,
              color: "#361836",
              fontSize: 11.5,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {badgeLabel}
          </span>
        )}
      </div>
    );

    return collapsed ? (
      <Tooltip content={hasBadge ? `${label} (${badge})` : label} relationship="label" positioning="after">
        {row}
      </Tooltip>
    ) : (
      row
    );
  };

  // ── PR4: Mail-Menü (in der Sidebar statt einer eigenen Mail-Sidebar) ──
  const selectFolder = (
    mailbox: string,
    folder: { id: string; displayName: string } | null,
  ) => {
    if (currentMailbox !== mailbox) setCurrentMailbox(mailbox);
    setActiveFolder(folder);
    if (isPhone) setDrawerOpen(false);
  };

  // Feste Standard-Ordner je Postfach (Posteingang/Entwürfe/Gesendet/Gelöscht/
  // Archiv). 'inbox' ist die Default-Auswahl (activeFolder=null ⇒ Posteingang).
  // Ungelesen-Badge nur am Posteingang (= unreadByMailbox).
  const renderFixedFolders = (mailbox: string, showFullTree: boolean): React.ReactNode[] => {
    const selectedKey = activeFolder?.id ?? "inbox";
    // Cluster POSTFACH (G5): Reihenfolge und Struktur kommen aus derselben
    // Funktion wie im „Verschieben nach…"-Menü. Sie liefert eine FLACHE Liste in
    // Vorordnung; das frühere rekursive Rendern erzeugte Kinder nur bei
    // aufgeklapptem Elternteil.
    const eintraege = buildMailFolderOrder(folderTrees[mailbox] ?? [], {
      includeCustom: showFullTree,
      // TERMINE (P6): die Sicht steht als eigene Zeile im Baum — sie ist der
      // vom PO verlangte Einstieg. Sie hängt am Posteingang und gilt deshalb
      // für JEDES lesbare Postfach (der Endpunkt ist postfach-gebunden und
      // prüft das Leserecht selbst). `unread: 0`: siehe mobileMailFolders.
      virtualTermine: { unread: 0 },
    });

    // Sichtbarkeit über `parentKey` statt über Rekursion: eine Zeile erscheint,
    // wenn sie auf Ebene 0 steht ODER ihr Elternteil selbst sichtbar UND
    // aufgeklappt ist. Die Regel liegt als reine Funktion in mailFolderTree.ts
    // und ist dort ohne DOM geprüft.
    const rows: React.ReactNode[] = [];

    for (const e of visibleFolderRows(eintraege, (key) =>
      expandedFolders.has(`${mailbox}:${key}`),
    )) {
      rows.push(
        <NavRow
          key={e.key}
          icon={mailFolderIcon(e)}
          label={e.label}
          collapsed={false}
          indent={1 + e.depth}
          active={currentMailbox === mailbox && selectedKey === e.key}
          onClick={() => selectFolder(mailbox, { id: e.key, displayName: e.label })}
          // Ungelesen-Zahl bleibt dem Posteingang vorbehalten — siehe
          // `inboxUnread` im MailContext. Eine Zahl je Ordner wäre eine andere
          // Größe und würde als Zählfehler gelesen.
          badge={e.alias === "inbox" ? unreadByMailbox[mailbox] ?? 0 : 0}
          // T-264: an eigenen Ordnern stattdessen die Gesamtzahl (grau).
          count={ordnerGesamtzahl(e)}
          expandable={e.hasChildren}
          expanded={expandedFolders.has(`${mailbox}:${e.key}`)}
          onToggleExpand={() => toggleFolder(mailbox, e.key)}
        />,
      );
      // T-65: Graph meldet Unterordner, das Backend hat sie aber nicht mehr
      // geladen (Call-Budget). Offen sagen statt einen leeren Pfeil zu zeigen.
      if (e.kinderFehlen && expandedFolders.has(`${mailbox}:${e.key}`)) {
        rows.push(
          <div
            key={`${e.key}:kinder-fehlen`}
            style={{
              marginLeft: 8 + (2 + e.depth) * 14,
              padding: "4px 10px",
              fontSize: 12,
              fontStyle: "italic",
              color: NAV.textMuted,
            }}
          >
            Unterordner konnten nicht geladen werden
          </div>,
        );
      }
    }
    return rows;
  };

  // B1: Rang-Semantik statt exaktem String-Match — SEND beinhaltet Lesen,
  // sonst verschwinden SEND-Postfächer (cbb@/schaden@) aus dem Mail-Menü.
  const readableMailboxes = mailboxes.filter(canReadMailbox);

  const mailMenuContent = () => (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Gepinnter Kopf: Zurück + Neue E-Mail (scrollt NICHT mit). */}
      <div style={{ flexShrink: 0, paddingTop: 8 }}>
        <NavRow
          icon={<ChevronLeft20Regular />}
          label="Zurück zum Menü"
          collapsed={false}
          onClick={() => setMailMenuOpen(false)}
        />
        <NavRow
          icon={<Add20Regular />}
          label="Neue E-Mail"
          collapsed={false}
          onClick={() => openComposeWindow(openWindow, { mailbox: currentMailbox })}
        />
        <div style={{ height: 1, background: NAV.divider, margin: "6px 16px" }} />
      </div>
      {/* Scroll-Zone: Postfächer × Ordner (inline scrollend). */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingBottom: 8 }}>
        {/* DVAG-Kontakt- und Schadenhistorie: stehen vor den Postfächern (PO 11.10.2026). Kein
            Outlook-Postfach — die Einträge kommen aus der KMVS-Datenbank; die
            Auswahl läuft über einen virtuellen Ordner, das Postfach bleibt. */}
        {dvagZaehler &&
          ([
            ["kontakt", dvagZaehler.ungelesen],
            ["schaden", dvagZaehler.schaden?.ungelesen ?? 0],
          ] as const).map(([bereich, ungelesen]) => (
            <div key={bereich} style={{ marginTop: 6 }}>
              <div
                style={{
                  padding: "6px 16px",
                  fontFamily: NAV.font,
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: NAV.sectionLabel,
                }}
              >
                {DVAG_BEREICH_TITEL[bereich]}
              </div>
              <NavRow
                icon={<MailInboxAll20Regular />}
                label="Posteingang"
                collapsed={false}
                indent={1}
                active={activeFolder?.id === dvagOrdnerKey("posteingang", bereich)}
                onClick={() => {
                  setActiveFolder({ id: dvagOrdnerKey("posteingang", bereich), displayName: DVAG_BEREICH_TITEL[bereich] });
                  if (isPhone) setDrawerOpen(false);
                }}
                badge={ungelesen}
              />
              <NavRow
                icon={<Archive20Regular />}
                label="Archiv"
                collapsed={false}
                indent={1}
                active={activeFolder?.id === dvagOrdnerKey("archiv", bereich)}
                onClick={() => {
                  setActiveFolder({
                    id: dvagOrdnerKey("archiv", bereich),
                    displayName: `${DVAG_BEREICH_TITEL[bereich]} · Archiv`,
                  });
                  if (isPhone) setDrawerOpen(false);
                }}
              />
            </div>
          ))}
        {readableMailboxes.length === 0 && (
          <div style={{ padding: "8px 18px", fontSize: 13, color: NAV.textMuted }}>
            Keine Postfächer.
          </div>
        )}
        {readableMailboxes.map((mb) => {
          const total = unreadByMailbox[mb.mailboxAddress] ?? 0;
          return (
            <div key={mb.id} style={{ marginTop: 6 }}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => selectFolder(mb.mailboxAddress, null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    selectFolder(mb.mailboxAddress, null);
                  }
                }}
                title={mb.mailboxAddress}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  padding: "6px 16px",
                  fontFamily: NAV.font,
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: NAV.sectionLabel,
                }}
              >
                <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {mb.displayName || mb.mailboxAddress}
                </span>
                {total > 0 && (
                  <span
                    style={{
                      minWidth: 20,
                      height: 18,
                      padding: "0 6px",
                      borderRadius: 999,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: NAV.accent,
                      color: "#361836",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {total}
                  </span>
                )}
              </div>
              {/* Wer den vollen Baum sieht, steht als Regel in
                  mailFolderTree.ts und ist dort ohne DOM geprüft. */}
              {renderFixedFolders(
                mb.mailboxAddress,
                zeigeVollenBaum({
                  isOwn: mb.isOwn,
                  isAdmin,
                  vollerOrdnerbaum: mb.vollerOrdnerbaum,
                }),
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // Eingeklapptes Mail-Menü: Ordner brauchen ihre Namen und passen nicht in die
  // schmale Leiste. Sie zeigt deshalb „Neue E-Mail" (sonst nirgends auf dem
  // Desktop) und darunter die normalen Menüpunkte.
  const mailMenuSchmal = () => (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div style={{ flexShrink: 0, paddingTop: 8 }}>
        <NavRow
          icon={<Add20Regular />}
          label="Neue E-Mail"
          collapsed
          onClick={() => openComposeWindow(openWindow, { mailbox: currentMailbox })}
        />
        <div style={{ height: 1, background: NAV.divider, margin: "6px 12px 0" }} />
      </div>
      {navContent(true, true)}
    </div>
  );

  // Gemeinsamer Menü-Inhalt für Sidebar (inline) und Overlay (Phone). Im Mail-
  // Modus (auf /emails, Menü offen) wird statt der Top-Level-Liste das Mail-Menü
  // gerendert — eingeklappt in seiner schmalen Form.
  const navContent = (collapsed: boolean, topLevelOnly = false): React.ReactNode =>
    mailMode && !topLevelOnly ? (
      collapsed ? mailMenuSchmal() : mailMenuContent()
    ) : (
      // `minHeight: 0` ist PFLICHT, nicht Kosmetik: Ein Flex-Kind hat
      // `min-height: auto` und schrumpft deshalb nicht unter seine
      // Inhaltshöhe — `overflowY: auto` griffe nie. Die Liste wüchse
      // stattdessen über die Sidebar hinaus, und weil die `overflow: hidden`
      // trägt, wären die untersten Punkte schlicht nicht mehr erreichbar.
      // Genau das passierte mit „CO intern" als zusätzlichem Eintrag auf
      // kurzen Bildschirmen.
      <nav style={{ display: "flex", flexDirection: "column", paddingTop: 8, paddingBottom: 8, flex: 1, minHeight: 0, overflowY: "auto" }}>
        {/* „Zahlungsverkehr" ist mobil bewusst ausgeblendet (User-Entscheidung 16). */}
        {[...items, ...trailingItems]
          .filter((i) => i.show && !(isPhone && i.value === "rechnungen"))
          .map((i) => (
          <NavRow
            key={i.value}
            icon={i.icon}
            label={i.label}
            active={activeValue === i.value}
            collapsed={collapsed}
            onClick={i.onClick}
            badge={i.badge}
          />
        ))}
        {/* Abschluss-Gruppe: dünne Linie, dann „CO intern". Die Linie wird nur
            gezeichnet, wenn darunter auch etwas steht — sonst hinge sie als
            Strich ohne Inhalt unter dem Menü. */}
        {abschlussItems.some((i) => i.show) && (
          <>
            <div
              aria-hidden
              style={{
                height: 1,
                background: NAV.divider,
                // Eingeklappt ist die Sidebar nur eine schmale Leiste — mit den
                // 16 px der ausgeklappten Ansicht bliebe von der Linie ein
                // Stummel übrig.
                margin: collapsed ? "8px 12px 6px" : "8px 16px 6px",
              }}
            />
            {abschlussItems
              .filter((i) => i.show)
              .map((i) => (
                <NavRow
                  key={i.value}
                  icon={i.icon}
                  label={i.label}
                  active={activeValue === i.value}
                  collapsed={collapsed}
                  onClick={i.onClick}
                  badge={i.badge}
                />
              ))}
          </>
        )}
      </nav>
    );

  // Kopf der Sidebar: Marken-Logo zentriert (ausgeklappt Wortmarke / eingeklappt
  // k'-Emblem) + runder Ein-/Ausklapp-Schalter (Chevron, dreht 180°). Der Toggle
  // sitzt ausgeklappt am rechten Rand (Wortmarke bleibt mittig). Gold-Trennlinie.
  const sidebarHeader = (collapsed: boolean) => {
    const toggle = (
      <Tooltip content={collapsed ? "Menü ausklappen" : "Menü einklappen"} relationship="label" positioning="after">
        <button
          type="button"
          aria-label={collapsed ? "Menü ausklappen" : "Menü einklappen"}
          onClick={() => (mailMode ? mailMenuKlappen() : setIsCollapsed((v) => !v))}
          style={{
            position: collapsed ? "static" : "absolute",
            right: collapsed ? undefined : 10,
            top: collapsed ? undefined : 14,
            flexShrink: 0,
            width: 30,
            height: 30,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            border: "1px solid rgba(255,255,255,0.16)",
            background: "rgba(255,255,255,0.05)",
            color: NAV.text,
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = NAV.hoverBg)}
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(255,255,255,0.05)")}
        >
          <span style={{ display: "flex", transform: collapsed ? "rotate(180deg)" : "none", transition: "transform 160ms ease" }}>
            <ChevronLeft20Regular />
          </span>
        </button>
      </Tooltip>
    );
    return (
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: collapsed ? 12 : 0,
          minHeight: collapsed ? 64 : 96,
          padding: collapsed ? "14px 0 12px" : "20px 14px 16px",
          flexShrink: 0,
          borderBottom: `1px solid ${NAV.divider}`,
        }}
      >
        {/* Logo-Klick → Dashboard (E1). */}
        <button
          type="button"
          onClick={() => handleNavigate("/")}
          aria-label="Zum Dashboard"
          title="Zum Dashboard"
          style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", maxWidth: collapsed ? undefined : "82%" }}
        >
          {collapsed ? (
            <img src={KmvsEmblem} alt="kmvs" style={{ height: 44, width: "auto", objectFit: "contain" }} />
          ) : (
            <img src={KmvsWordmark} alt="kmvs · Courté J. & Co. KG" style={{ width: 150, maxWidth: "100%", height: "auto", objectFit: "contain" }} />
          )}
        </button>
        {toggle}
      </div>
    );
  };

  // Sidebar-Footer (Desktop/Tablet, unten in der <aside>). SP2:
  //  - Ausgeklappt (Desktop): Circle + „Nachname, Vorname" (→ /profil) + ⋮
  //    (→ Popover NUR „Abmelden"). Strikte Trennung Name=Profil / ⋮=Abmelden.
  //  - Eingeklappt (Tablet-Rail / collapsed): nur Circle, Tap → Popover mit
  //    BEIDEN Items (E3 — kein Name-Klickziel verfügbar).
  // „Abmelden" läuft immer über requestLogout (Tagesabschluss-Gate erhalten).
  const sidebarFooter = (collapsed: boolean) => (
    <div
      style={{
        flexShrink: 0,
        borderTop: "1px solid rgba(255,255,255,0.08)",
        padding: collapsed ? "8px 0" : "8px 10px",
        display: "flex",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "space-between",
        gap: 8,
      }}
    >
      {collapsed ? (
        <UserMenuPopover
          onLogout={requestLogout}
          onProfile={() => handleNavigate("/profil")}
          showProfile
          showLogout
          align="start"
          trigger={({ toggle, ref }) => (
            <button
              ref={ref}
              type="button"
              onClick={toggle}
              aria-label="Benutzermenü"
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
            >
              <UserCircle placement="sidebar" />
            </button>
          )}
        />
      ) : (
        <>
          <button
            type="button"
            onClick={() => handleNavigate("/profil")}
            title={displayName(user as any)}
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 44,
              display: "flex",
              alignItems: "center",
              gap: 10,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: "0 6px",
              borderRadius: 8,
              color: NAV.text,
              font: `600 14px/1.2 ${NAV.font}`,
              textAlign: "left",
            }}
            onMouseEnter={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.backgroundColor = NAV.hoverBg)
            }
            onMouseLeave={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent")
            }
          >
            <UserCircle placement="sidebar" />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayName(user as any)}
            </span>
          </button>
          <UserMenuPopover
            onLogout={requestLogout}
            showProfile={false}
            showLogout
            align="start"
            trigger={({ toggle, ref }) => (
              <button
                ref={ref}
                type="button"
                onClick={toggle}
                aria-label="Benutzermenü"
                title="Menü"
                style={{
                  flexShrink: 0,
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  borderRadius: 8,
                  color: NAV.text,
                  fontSize: 20,
                  lineHeight: 1,
                }}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLButtonElement).style.backgroundColor = NAV.hoverBg)
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent")
                }
              >
                {"⋮"}
              </button>
            )}
          />
        </>
      )}
    </div>
  );

  // Einheitliche Topbar-Icon-Schaltflächen (38×38, weiße Fläche).
  const iconBtnStyle: React.CSSProperties = {
    flexShrink: 0,
    width: 38,
    height: 38,
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: TOPBAR.iconBtnBg,
    border: `1px solid ${TOPBAR.iconBtnBorder}`,
    color: TOPBAR.iconBtnFg,
    cursor: "pointer",
  };

  // Runde Verlaufs-Navigation (Zurück/Vorwärts): Kreis, Aubergine/Lila-Fläche,
  // weißes Icon. Ersetzt die per-View „Zurück"-Buttons.
  const navBtnStyle: React.CSSProperties = {
    flexShrink: 0,
    width: 38,
    height: 38,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#7d2169",
    border: "none",
    color: "#ffffff",
    cursor: "pointer",
    transition: "background 120ms ease",
  };

  // Phone: kompakte Variante — beide Pfeile als EIN gruppiertes Segment (Pille),
  // schmaler als die Desktop-Kreise, mit dünneren 16er-Pfeilen (C7).
  const phoneNavPillStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    height: 32,
    background: "#7d2169",
    borderRadius: 16,
    overflow: "hidden",
  };
  const phoneNavSegStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 34,
    height: 32,
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#ffffff",
  };
  const phoneNavDividerStyle: React.CSSProperties = {
    width: 1,
    alignSelf: "stretch",
    background: "rgba(255,255,255,0.25)",
  };

  // Ehrliche Zurück/Vorwärts-Zustände aus dem In-App-Stack (D1/D2).
  const desktopMaximizedBack = hasMaximizedWindow && !isPhoneOrTablet;
  const backEnabled = navHist.canGoBack || desktopMaximizedBack;
  const fwdEnabled = navHist.canGoForward;
  const backTitle = desktopMaximizedBack
    ? "Vollbild verlassen"
    : backEnabled
      ? `Zurück zu ${navHist.backLabel}`
      : "Zurück";
  const fwdTitle = fwdEnabled ? `Vorwärts zu ${navHist.forwardLabel}` : "Vorwärts";
  const goForward = () => navigate(1);

  return (
    <div
      className="kmvs-dvh-screen"
      style={{
        width: "100%",
        // Tastatur offen (iOS): Shell an die sichtbare Fläche über der Tastatur
        // hängen, damit die Kopfleiste oben bleibt (hooks/useBildschirmTastatur).
        ...(tastatur && {
          position: "fixed",
          left: 0,
          top: tastatur.top,
          height: tastatur.height,
        }),
        display: "flex",
        flexDirection: "row",
        fontSize: "16px",
        fontFamily: NAV.font,
      }}
    >
      {/* Sidebar (Desktop/Tablet) — volle Höhe links, Plum-Verlauf. */}
      {!isPhone && (
        <aside
          style={{
            width: effectiveCollapsed ? NAV.widthCollapsed : NAV.widthExpanded,
            minWidth: effectiveCollapsed ? NAV.widthCollapsed : NAV.widthExpanded,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            background: NAV.bgGradient,
            color: NAV.text,
            transition: "width 220ms ease",
            overflow: "hidden",
          }}
        >
          {sidebarHeader(effectiveCollapsed)}
          {navContent(effectiveCollapsed)}
          {sidebarFooter(effectiveCollapsed)}
        </aside>
      )}

      {/* Phone: Vollbild-Menü (ersetzt den 280px-Drawer, A5) — das komplette Menü
          wie in der Desktop-Sidebar: Logo-Kopf (→ Dashboard) + Schließen, alle
          Menüpunkte (Top-Level, Badges als Punkt), Footer mit Profil/Abmelden
          (Logout-Gate greift). Geöffnet über den „Menü"-Punkt der Bottom-Leiste. */}
      {isPhone && drawerOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            // Über der Bottom-Leiste (z1000), unter den Anruf-Portalen (z1000000).
            zIndex: 1100,
            display: "flex",
            flexDirection: "column",
            background: NAV.bgGradient,
            color: NAV.text,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: 64,
              padding: "0 12px 0 16px",
              flexShrink: 0,
              borderBottom: `1px solid ${NAV.divider}`,
            }}
          >
            {/* Logo-Klick → Dashboard (E1) — handleNavigate schließt das Menü mit. */}
            <button
              type="button"
              onClick={() => handleNavigate("/")}
              aria-label="Zum Dashboard"
              title="Zum Dashboard"
              style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", display: "flex", alignItems: "center" }}
            >
              <img src={KmvsWordmark} alt="kmvs · Courté J. & Co. KG" style={{ height: 36, maxWidth: 150, width: "auto", objectFit: "contain" }} />
            </button>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              aria-label="Menü schließen"
              title="Schließen"
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid rgba(255,255,255,0.16)",
                background: "rgba(255,255,255,0.05)",
                color: NAV.text,
                cursor: "pointer",
                fontSize: 20,
              }}
            >
              <Dismiss20Regular />
            </button>
          </div>
          {/* Top-Level-Menü erzwingen (auf /emails NICHT das Mail-Menü zeigen —
              das läuft ab SP4 über die kontextuelle MobileMailBar). minHeight:0
              macht die Liste intern scrollbar, damit der Footer sichtbar bleibt. */}
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {navContent(false, true)}
          </div>
          {sidebarFooter(false)}
        </div>
      )}

      {/* Rechte Spalte: Topbar + aktiver Anruf + Content. */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}>
      {/* Topbar (hell, transluzent) — Seitentitel (Playfair) links, Aktionen rechts. */}
      <header
        style={{
          color: TOPBAR.title,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          // Höhe = EINZIGE Quelle `--topbar-h` (= 64px + obere Safe-Area). Der
          // paddingTop schiebt den Inhalt unter Notch/Statusleiste; die 64px
          // sichtbare Kopfzeile bleiben darunter erhalten (Fallback 64px ohne
          // Notch bzw. ohne viewport-fit=cover).
          // ⚠️ border-box ist PFLICHT: es gibt keinen globalen box-sizing-Reset,
          // und der Header liegt nicht unter `.kvp-rd`. Ohne border-box würde der
          // content-box-Default den paddingTop ZUSÄTZLICH zur height addieren
          // (Höhe = 64+2×Inset statt 64+Inset), während die --topbar-h-Konsumenten
          // (ChatPage/NewRiskPage/vorgangDetail) nur 64+Inset abziehen → Inhalt
          // rutscht auf Notch-Geräten hinter Top-/Bottom-Leiste. Mit border-box
          // enthält die height den paddingTop → gerenderte Höhe == --topbar-h.
          boxSizing: "border-box",
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingRight: isPhone ? 12 : 24,
          paddingBottom: 0,
          paddingLeft: isPhone ? 12 : 24,
          gap: 16,
          height: "var(--topbar-h, 64px)",
          lineHeight: "1.875rem", // 30px
          flexShrink: 0,
          background: TOPBAR.bg,
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          borderBottom: `1px solid ${TOPBAR.border}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          {/* Hamburger entfällt auf dem Phone — die Navigation liegt jetzt in der
              fixen Bottom-Leiste (MobileBottomNav) + Vollbild-Menü (SP3). */}
          {/* Verlaufs-Navigation (Zurück/Vorwärts) — In-App-Verlauf: ausgegraut
              ohne Ziel (D1), Tooltip zeigt grob das Ziel (D2). Phone: kompakte
              Pille mit dünneren Pfeilen (C7). */}
          {isPhone ? (
            <div style={phoneNavPillStyle}>
              <button
                type="button"
                aria-label="Zurück"
                aria-disabled={!backEnabled}
                title={backTitle}
                onClick={backEnabled ? goBack : undefined}
                style={{
                  ...phoneNavSegStyle,
                  opacity: backEnabled ? 1 : 0.35,
                  cursor: backEnabled ? "pointer" : "default",
                }}
              >
                <ArrowLeft16Regular />
              </button>
              <span style={phoneNavDividerStyle} aria-hidden="true" />
              <button
                type="button"
                aria-label="Vorwärts"
                aria-disabled={!fwdEnabled}
                title={fwdTitle}
                onClick={fwdEnabled ? goForward : undefined}
                style={{
                  ...phoneNavSegStyle,
                  opacity: fwdEnabled ? 1 : 0.35,
                  cursor: fwdEnabled ? "pointer" : "default",
                }}
              >
                <ArrowRight16Regular />
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <button
                type="button"
                aria-label="Zurück"
                aria-disabled={!backEnabled}
                title={backTitle}
                onClick={backEnabled ? goBack : undefined}
                style={{
                  ...navBtnStyle,
                  opacity: backEnabled ? 1 : 0.35,
                  cursor: backEnabled ? "pointer" : "default",
                }}
                onMouseEnter={backEnabled ? (e) => (e.currentTarget.style.background = "#6e1c5c") : undefined}
                onMouseLeave={backEnabled ? (e) => (e.currentTarget.style.background = "#7d2169") : undefined}
              >
                <ArrowLeft20Regular />
              </button>
              <button
                type="button"
                aria-label="Vorwärts"
                aria-disabled={!fwdEnabled}
                title={fwdTitle}
                onClick={fwdEnabled ? goForward : undefined}
                style={{
                  ...navBtnStyle,
                  opacity: fwdEnabled ? 1 : 0.35,
                  cursor: fwdEnabled ? "pointer" : "default",
                }}
                onMouseEnter={fwdEnabled ? (e) => (e.currentTarget.style.background = "#6e1c5c") : undefined}
                onMouseLeave={fwdEnabled ? (e) => (e.currentTarget.style.background = "#7d2169") : undefined}
              >
                <ArrowRight20Regular />
              </button>
            </div>
          )}
          <h1
            title={pageTitle || undefined}
            style={{
              margin: 0,
              fontFamily: NAV.fontHead,
              fontWeight: 500,
              // Phone kompakter (18 statt 20), damit der Titel neben den
              // Verlaufs-Buttons nicht trunkiert (C6, kein Zeilenumbruch).
              fontSize: isPhone ? 18 : 24,
              color: TOPBAR.title,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {pageTitle}
          </h1>
        </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            alignSelf: "stretch",
          }}
        >
          {/* Globale Suche (Desktop): volle Pille mit ⌘K-Kürzel. Öffnet die
              Command-Palette (globale Suche). */}
          {topbar.showSearchPill && (
            <button
              type="button"
              aria-label="Suche"
              title="Suchen (⌘K)"
              onClick={() => setPaletteOpen(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                height: 38,
                width: 280,
                maxWidth: "30vw",
                padding: "0 12px",
                borderRadius: 10,
                border: `1px solid ${TOPBAR.iconBtnBorder}`,
                background: TOPBAR.iconBtnBg,
                color: "#9a8a9c",
                cursor: "pointer",
                font: `400 14px/1 ${NAV.font}`,
              }}
            >
              <Search20Regular />
              <span style={{ flex: 1, textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Alles durchsuchen…</span>
              <kbd style={{ fontFamily: NAV.font, fontSize: 11, fontWeight: 600, color: "#6f5c70", background: "#f1edf0", border: `1px solid ${TOPBAR.iconBtnBorder}`, borderRadius: 6, padding: "2px 6px" }}>⌘K</kbd>
            </button>
          )}

          {/* Globale Suche (Tablet, G1): kompaktes Such-Icon statt der breiten
              Pille — die Suchleiste (Command-Palette) klappt erst auf Klick auf,
              damit der Header auf 769–1024px nicht bricht. */}
          {topbar.showSearchIcon && (
            <button
              type="button"
              aria-label="Suche"
              title="Suchen (⌘K)"
              style={iconBtnStyle}
              onClick={() => setPaletteOpen(true)}
            >
              <Search20Regular />
            </button>
          )}

          {/* Telefon — öffnet das globale Schnellwahl-Overlay (Suche/Zuletzt/
              Roh-Nummer, SP5). Ersetzt das frühere Quick-Dial-Popover; nur mit
              calls:read. Entfällt auf dem Tablet (G1) — dort über die
              Command-Palette „Telefon öffnen" bzw. das Sidebar-Menü erreichbar. */}
          {topbar.showPhoneButton && hasPermission("calls:read") && (
            <button
              type="button"
              aria-label="Schnellwahl"
              title="Schnellwahl"
              style={iconBtnStyle}
              onClick={openSchnellwahl}
            >
              <Call20Regular />
            </button>
          )}

          {/* Neu anlegen (+) — eigenes Menü mit acht Nomen (Kontakt, Risiko,
              Vertrag, Schaden, Vorgang, Termin, Mail, Dokument) und den
              Vorfrage-Dialogen davor; siehe components/neuAnlage. */}
          <NeuAnlageKnopf
            isPhone={isPhone}
            knopfStyle={iconBtnStyle}
            mailbox={currentMailbox}
            onNavigiert={isPhone ? () => setDrawerOpen(false) : undefined}
          />

          {/* Einstellungen */}
          {!isPhone && (
            <button type="button" aria-label="Einstellungen" title="Einstellungen" style={iconBtnStyle} onClick={() => handleNavigate("/settings")}>
              <Settings20Regular />
            </button>
          )}

          {/* Hilfe-/Tutorial-Button (Fragezeichen) bewusst ersatzlos entfernt —
              der Trigger war auf der hellen Topbar unsichtbar (weiß auf weiß).
              Die Komponente tutorials/HelpMenu bleibt im Code (Wiedereinstieg
              später z. B. über Profil/Einstellungen). */}

          {/* Mitteilungszentrum (Glocke) — hervorgehobener Button mit Label
              (auf Phone nur das Icon). Inhalt/Verhalten unverändert. */}
          <Popover open={notifOpen} onOpenChange={(_, d) => setNotifOpen(d.open)}>
            <PopoverTrigger disableButtonEnhancement>
              <div
                role="button"
                tabIndex={0}
                style={{
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: isPhone ? 0 : 10,
                  position: "relative",
                  // Phone: runder 38×38-Kreis, vertikal mittig (C3/C4) — das
                  // frühere alignSelf:stretch ankerte die 38px-Höhe oben im
                  // 64px-Header und padding machte eine Pille statt Kreis.
                  alignSelf: isPhone ? "center" : "stretch",
                  width: isPhone ? 38 : undefined,
                  height: isPhone ? 38 : "100%",
                  flexShrink: 0,
                  padding: isPhone ? 0 : "0 22px",
                  marginRight: isPhone ? 0 : -24,
                  marginLeft: isPhone ? 0 : 4,
                  boxSizing: "border-box",
                  borderRadius: isPhone ? "50%" : 0,
                  background: "linear-gradient(180deg, #4a2a50 0%, #2f1733 100%)",
                  color: "#ffffff",
                  border: "none",
                  fontFamily: "'Hanken Grotesk', 'Segoe UI', Tahoma, sans-serif",
                  fontWeight: 700,
                  fontSize: 14,
                  letterSpacing: "0.01em",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                }}
                title="Mitteilungszentrum"
                onClick={() => pushService.ensureSubscribed({ interactive: true }).catch(() => undefined)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setNotifOpen((v) => !v);
                  }
                }}
              >
                <Alert20Regular />
                {!isPhone && <span>Mitteilungszentrum</span>}
                {/* Badge: Desktop goldene Zähler-Pille, Phone nur ein goldener
                    Punkt (A7 — mobil Punkt statt Zahl, wie die Rail-Badges). */}
                {glockenZahl > 0 &&
                  (isPhone ? (
                    <span
                      aria-hidden
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        background: NAV.accent,
                        boxShadow: "0 0 0 2px rgba(255,255,255,0.9)",
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 22,
                        height: 22,
                        padding: "0 6px",
                        borderRadius: 999,
                        background: NAV.accent,
                        color: "#361836",
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {glockenZahl}
                    </span>
                  ))}
              </div>
            </PopoverTrigger>
            <PopoverSurface
              style={{
                // Phone: (nahezu) vollbreites Sheet statt schmalem Popover, damit
                // Mitteilungs-Einträge nicht abgeschnitten werden (kein H-Scroll @375px).
                minWidth: isPhone ? "calc(100vw - 24px)" : "330px",
                maxWidth: isPhone ? "calc(100vw - 24px)" : "400px",
                width: isPhone ? "calc(100vw - 24px)" : undefined,
                boxSizing: "border-box",
                padding: "16px",
                background: "rgba(255,255,255,0.82)",
                backdropFilter: "blur(20px) saturate(1.5)",
                WebkitBackdropFilter: "blur(20px) saturate(1.5)",
                border: "1px solid rgba(125,33,105,0.14)",
                borderRadius: 18,
                boxShadow: "0 24px 60px -20px rgba(58,12,49,0.40), 0 2px 12px rgba(58,12,49,0.10)",
                fontFamily: "'Hanken Grotesk', 'Segoe UI', Tahoma, sans-serif",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                <div style={{ fontWeight: 700, fontSize: "16px", color: "#241522" }}>
                  {noCenterItems ? "Keine neuen Mitteilungen" : "Mitteilungszentrum"}
                </div>
                {glockenZahl > 0 && (
                  <button
                    onClick={markAllNotificationsRead}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#7d2169",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                      // Touch-Target (≥44px) auf Phone, ohne die Desktop-Optik zu stören.
                      minHeight: isPhone ? 44 : undefined,
                      padding: isPhone ? "0 6px" : 0,
                      flexShrink: 0,
                    }}
                  >
                    Alle gelesen
                  </button>
                )}
              </div>
              <NotificationSetupHinweis aktiv={isAuthenticated} variante="karte" />
              {/* Nach Themen gegliedert (01.11.2026) — dieselbe Liste wie die Dashboard-Karte. */}
              <div style={{ maxHeight: isPhone ? "70vh" : "min(70vh, 560px)", overflowY: "auto", margin: "0 -4px", padding: "0 4px" }}>
                <MitteilungszentrumListe
                  variante="glocke"
                  aktiv={notifOpen}
                  touch={isPhone}
                  onOeffnen={(link) => {
                    setNotifOpen(false);
                    handleNavigate(link);
                  }}
                />
              </div>
            </PopoverSurface>
          </Popover>

          {/* Benutzerbild / Info — NUR auf dem Phone in der Topbar (der Drawer hat
              keinen Footer). Desktop/Tablet haben das Benutzermenü im Sidebar-
              Footer bzw. in der eingeklappten Rail. „Abmelden" läuft über das
              Tagesabschluss-Gate (requestLogout), NIE direkt signOut. */}
          {isPhone && (
            <UserMenuPopover
              onLogout={requestLogout}
              onProfile={() => handleNavigate("/profil")}
              showProfile
              showLogout
              align="end"
              trigger={({ toggle, ref }) => (
                <button
                  ref={ref}
                  type="button"
                  onClick={toggle}
                  aria-label="Benutzermenü"
                  style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <UserCircle placement="topbar" />
                </button>
              )}
            />
          )}
        </div>
      </header>

      {/* Aktiver Anruf (minimiert): die ActiveCallBar wird seit P5 global (per
          Portal, schwebend unten) in App.tsx neben dem CallWidget gerendert —
          NICHT mehr hier im Layout-Fluss. */}

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflowX: "auto",
          position: "relative",
        }}
      >
          {/* Neue Version ausgeliefert → „Jetzt neu laden" (components/update). */}
          <UpdateHinweis aktiv={isAuthenticated} />
          {/* Erlaubnis-Hinweis: Edge/Chrome zeigen die Nachfrage nur als leicht zu
              uebersehendes Glocken-Symbol in der Adressleiste — ohne diesen Balken
              wird nie jemand gefragt und niemand bekommt Benachrichtigungen. */}
          {/* Auf dem Smartphone keine Einblendungen (15.10.2026) — die Glocke bleibt. */}
          <NotificationSetupHinweis aktiv={isAuthenticated && !isPhone} variante="balken" />
          <main
            style={{
              flex: 1,
              // `minHeight: 0` is what makes <main> the real scroll container:
              // without it a flex child keeps its content height (min-height:auto)
              // and the *parent* wrapper scrolls instead, so any `position: sticky`
              // header inside the page (e.g. ContactDetails) references <main> but
              // <main> never scrolls — the header scrolls away. With minHeight:0
              // <main> is bounded and scrolls internally, so sticky headers stick.
              minHeight: 0,
              backgroundColor: "#f8f8f8",
              padding: "4px",
              paddingTop: "0px",
              // Unten Platz fürs Fenster-Dock UND die mobile Bottom-Leiste
              // reservieren, damit der letzte Content nicht hinter ihnen
              // verschwindet (Fallback 4px = bisheriger Rand ohne Dock;
              // --mobile-nav-h fällt auf 0 zurück, sobald keine Leiste da ist).
              paddingBottom: "calc(var(--wm-dock-h, 4px) + var(--mobile-nav-h, 0px))",
              overflow: "auto",
            }}
          >
            {children}
          </main>
          {/* Window-Layer als absolutes Overlay über den Main-Content-Bereich,
              außerhalb des scrollbaren <main> — damit die Taskbar fix unten
              klebt und der Layer immer genau die Breite/Höhe des Content-
              Bereichs hat (egal ob Sidebar collapsed oder expanded ist). */}
          <WindowLayer />
        </div>
      </div>

      {/* Globaler Melden-Knopf unten rechts (Problem/Idee) — Portal. */}
      <MeldeButton />

      {/* Globale Suche / Command-Palette (⌘K) — Portal. */}
      <CommandPalette />

      {/* Mobile Bottom-Navigation (nur Phone ≤768, immer fix — Entscheidung 10/15).
          Bei geöffnetem Vollbild-Fenster ausgeblendet (Entscheidung 12).
          Auch auf /emails bleibt die App-Navigation stehen (User 03.11.2026):
          Postfach/Ordner, Suche, Filter und „Neue Mail" liegen dort jetzt in
          der MailView selbst (Kopf mit Oval, runder Knopf) — wie Outlook iOS. */}
      {showMobileBottomNav && !tastatur && (
        // Bei offener Tastatur weg: sonst klebt die Leiste über der Tastatur
        // und frisst die ohnehin knappe Höhe.
        <MobileBottomNav
          items={phoneNavItems}
          activeValue={activeValue}
          onOpenMenu={() => setDrawerOpen(true)}
          menuOpen={drawerOpen}
        />
      )}

      {/* Abmelde-Sperre v1 (AC1c): hartes In-App-Logout-Gate. Blockiert den
          Logout, solange nicht abgehakte Mitteilungen vorliegen — „Alle abhaken"
          löst das in einem Schritt (T-364). */}
      <Dialog
        open={logoutGuardOpen}
        onOpenChange={(_, d) => setLogoutGuardOpen(d.open)}
      >
        <DialogSurface
          style={{
            // Phone: vollflächig/breit & lesbar statt schmaler Dialog.
            maxWidth: isPhone ? "calc(100vw - 24px)" : "min(480px, 94vw)",
            width: isPhone ? "calc(100vw - 24px)" : undefined,
            boxSizing: "border-box",
          }}
        >
          <DialogBody>
            <DialogTitle>Abmelden nicht möglich</DialogTitle>
            <DialogContent>
              Bitte löse vor dem Abmelden deinen Tagesabschluss auf:
              <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                {unreadNotifications.length > 0 && (
                  <li>
                    {unreadNotifications.length}{" "}
                    {unreadNotifications.length === 1
                      ? "nicht abgehakte Mitteilung"
                      : "nicht abgehakte Mitteilungen"}{" "}
                    — im Mitteilungszentrum (Glocke) prüfen und abhaken.
                    Hast du sie schon gesehen, reicht „Alle abhaken".
                  </li>
                )}
                {!!dayClose?.dueTwv && (
                  <li>
                    {dayClose.dueTwv}{" "}
                    {dayClose.dueTwv === 1 ? "fällige TWV" : "fällige TWVs"}{" "}
                    — erledigen oder auf später setzen.
                  </li>
                )}
                {!!dayClose?.dueFristen && (
                  <li>
                    {dayClose.dueFristen}{" "}
                    {dayClose.dueFristen === 1 ? "fällige Frist" : "fällige Fristen"}{" "}
                    — erledigen oder auf später setzen.
                  </li>
                )}
              </ul>
              <div style={{ marginTop: 8, opacity: 0.8 }}>
                Sobald alles aufgelöst ist, kannst du dich abmelden.
              </div>
            </DialogContent>
            <DialogActions
              style={
                isPhone
                  ? { display: "flex", flexDirection: "column", gap: 8, alignItems: "stretch" }
                  : undefined
              }
            >
              <Button
                appearance="secondary"
                onClick={() => setLogoutGuardOpen(false)}
                style={isPhone ? { width: "100%", minHeight: 44 } : undefined}
              >
                Abbrechen
              </Button>
              {(!!dayClose?.dueTwv || !!dayClose?.dueFristen) && (
                <Button
                  appearance="secondary"
                  onClick={goCheckVorgaenge}
                  style={isPhone ? { width: "100%", minHeight: 44 } : undefined}
                >
                  Vorgänge öffnen
                </Button>
              )}
              {unreadNotifications.length > 0 && (
                <Button
                  appearance="secondary"
                  onClick={() => void alleAbhakenAusGate()}
                  style={isPhone ? { width: "100%", minHeight: 44 } : undefined}
                >
                  {dayClose?.blocking ? "Alle abhaken" : "Alle abhaken und abmelden"}
                </Button>
              )}
              {unreadNotifications.length > 0 && (
                <Button
                  appearance="primary"
                  onClick={goCheckNotifications}
                  style={isPhone ? { width: "100%", minHeight: 44 } : undefined}
                >
                  Mitteilungen prüfen
                </Button>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default NavigationLayout;
